import { useState } from 'react';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { MARKS } from '@hotjar/webapp/performance';
import { verifyEmailCode } from '@hotjar/webapp/domains/common/verify-email/api';
import { createUser } from '@hotjar/webapp/domains/acquisition/register/api';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { mark, measureAndLog } from '@hotjar/performance';
import type { SignupFormData } from '@hotjar/webapp/features/Registration/steps/SelectSignUpMethodStep/SelectSignUpMethodStep';
import type { VerifyEmailCodeResponseDto } from '@hotjar/webapp/domains/common/verify-email/model';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { ONBOARDING } from '@hotjar/webapp/constants/routes/onboarding';
import type { SignupMethodSettings } from '@hotjar/webapp/features/Registration/hooks/useRegisterQueryParams';
import {
  usePopulateStateParams,
  useRegisterQueryParams,
} from '@hotjar/webapp/features/Registration/hooks/useRegisterQueryParams';
import {
  DEFAULT_PRICING_TABLE_NUMBER,
  DEFAULT_ERROR_CODE,
  DEFAULT_PLAN,
} from '@hotjar/webapp/features/Registration/common/constants';
import { refreshFromServer } from '@hotjar/webapp/domains/common/user-data';
import type { RegistrationResultDto } from '@hotjar/webapp/domains/acquisition/register/dto';
import { useRegistrationCampaign } from '@hotjar/webapp/hooks/useCampaigns';
import { useAnonymousId } from '@hotjar/webapp/hooks/useAnonymousId';

interface EmailCodeValidation {
  isSubmitInProgress: boolean;
  codeError: string;
  onInputChange: (valuesArr: number[]) => void;
  onFormSubmit: (code?: string) => void;
}

interface EmailCodeValidationArguments {
  signupMethod: SignupMethodSettings;
  userDetailsWithVid: SignupFormData & { emailVerificationVid: string };
  userCreationTracking: {
    trackOnEmailCodeSubmit: (isValid: boolean, method: string) => void;
    trackOnEmailCodeComplete: () => void;
    trackUserCreation: (user: RegistrationResultDto) => Promise<void>;
    trackUserCreationError: (err: Error) => void;
  };
}

const processValidationError = (err: AxiosError<VerifyEmailCodeResponseDto>) => {
  const hasErrorData =
    err?.response?.data && Object.prototype.hasOwnProperty.call(err?.response?.data, 'can_retry');
  let errorCodeFromResponse = DEFAULT_ERROR_CODE;

  if (hasErrorData) {
    errorCodeFromResponse = err?.response?.data.can_retry
      ? 'common:registration.verify-email.incorrect-code'
      : 'common:registration.verify-email.error-unretriable';
  }

  return errorCodeFromResponse;
};

export const useEmailCodeValidationForm = ({
  signupMethod,
  userDetailsWithVid,
  userCreationTracking,
}: EmailCodeValidationArguments): EmailCodeValidation => {
  const {
    trackOnEmailCodeSubmit,
    trackOnEmailCodeComplete,
    trackUserCreation,
    trackUserCreationError,
  } = userCreationTracking;
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
  const stateParamsObject = usePopulateStateParams({ signupMethod: signupMethod.method });
  const { campaignId, pricingTable } = useRegisterQueryParams();
  const { data: campaign } = useRegistrationCampaign(campaignId);
  const { anonymousId } = useAnonymousId();
  const { i18n } = useTranslation();
  const currentLocale = i18n.languages?.[0];

  const onInputChange = (valuesArr: number[]) => {
    const newCode = valuesArr.join('');
    const codeHasChanged = newCode !== code;
    setCode(newCode);

    if (newCode && codeHasChanged) {
      setCodeError('');
    }
  };

  const handleUserCreationError = (err: Error) => {
    setCodeError(DEFAULT_ERROR_CODE);
    trackUserCreationError(err);
    throw err;
  };
  const onFormSubmit = (codeValue?: string) => {
    const codeToVerify: string = codeValue || code;

    if (!codeToVerify || codeToVerify.length < 6) {
      setCodeError('common:registration.verify-email.enter-valid-code');
      return;
    }

    if (isSubmitInProgress) return;

    setIsSubmitInProgress(true);

    mark(MARKS.SIGN_UP_VERIFY_EMAIL_SUBMISSION_START);
    setCodeError('');

    verifyEmailCode({
      code: codeToVerify,
      vid: userDetailsWithVid.emailVerificationVid,
    })
      .then(
        () => {
          trackOnEmailCodeSubmit(true, 'code');
          return {
            ...userDetailsWithVid,
            campaignHash: campaign?.hash || false,
            plan: (campaign?.valid && campaign?.plan) || DEFAULT_PLAN,
            pricingTable: Number(pricingTable) || DEFAULT_PRICING_TABLE_NUMBER,
            emailVerificationCode: codeToVerify,
            persistentAnonymousId: anonymousId as string,
            locale: currentLocale,
          };
        },
        (err) => {
          const errorFromResponseCode = processValidationError(err);
          setCodeError(errorFromResponseCode);

          trackOnEmailCodeSubmit(false, 'code');

          throw err;
        },
      )
      .then((userRegistrationData) =>
        createUser(userRegistrationData).then(trackUserCreation, handleUserCreationError),
      )
      .then(refreshFromServer)
      .then(() => {
        trackOnEmailCodeComplete();
        browserHistory.replace(getRoute(ONBOARDING.path, stateParamsObject));

        // Perf: Measure how long it took to complete the second step of sign up
        measureAndLog({
          name: MARKS.SIGN_UP_VERIFY_EMAIL_SUBMISSION_TIME,
          startMark: MARKS.SIGN_UP_VERIFY_EMAIL_SUBMISSION_START,
        });
      }, handleUserCreationError)
      .finally(() => {
        setIsSubmitInProgress(false);
      });
  };

  return {
    codeError,
    isSubmitInProgress,
    onInputChange,
    onFormSubmit,
  };
};
